import { format as formatDate } from 'date-fns';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface DownloadOptions {
  filename?: string;
  format?: 'pdf' | 'png';
  quality?: number;
  scale?: number;
  isDarkMode?: boolean;
  reportType?: 'soc-sim' | 'threat-hunting';
}

const getReportName = (reportType: 'soc-sim' | 'threat-hunting' = 'soc-sim') => {
  const baseName =
    reportType === 'soc-sim' ? 'tryhackme_soc_sim_simulator_report' : 'tryhackme_threat_hunting_simulator_report';
  return `${baseName}_${formatDate(new Date(), 'yyyy_MM_dd_HH_mm_ss')}`;
};

export const downloadDashboard = async (element: HTMLElement, options: DownloadOptions = {}): Promise<void> => {
  const {
    filename = getReportName(options.reportType),
    format = 'pdf',
    quality = 1,
    scale = 2,
    isDarkMode = false,
  } = options;

  try {
    const backgroundColor = isDarkMode ? '#151C2B' : '#ffffff';

    await new Promise((resolve) => setTimeout(resolve, 1000));

    const canvas = await html2canvas(element, {
      allowTaint: true,
      useCORS: true,
      scale,
      backgroundColor,
      logging: false,
      width: element.scrollWidth,
      height: element.scrollHeight,
      onclone: (clonedDoc) => {
        const downloadButtons = clonedDoc.querySelectorAll(
          '[data-testid="download-button"]',
        ) as NodeListOf<HTMLElement>;
        downloadButtons.forEach((button) => {
          button.style.display = 'none';
        });

        const allButtons = clonedDoc.querySelectorAll('button') as NodeListOf<HTMLElement>;
        allButtons.forEach((button) => {
          if (button.textContent?.includes('Download')) {
            button.style.display = 'none';
          }
        });
      },
    });

    if (format === 'png') {
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL('image/png', quality);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const imgData = canvas.toDataURL('image/png', quality);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [canvas.width / scale, canvas.height / scale],
      });

      const imgWidth = canvas.width / scale;
      const imgHeight = canvas.height / scale;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const scaleX = pageWidth / imgWidth;
      const scaleY = pageHeight / imgHeight;
      const pdfScale = Math.min(scaleX, scaleY);

      const scaledWidth = imgWidth * pdfScale;
      const scaledHeight = imgHeight * pdfScale;

      const x = (pageWidth - scaledWidth) / 2;
      const y = (pageHeight - scaledHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
      pdf.save(`${filename}.pdf`);
    }
  } catch (error) {
    throw new Error('Failed to download dashboard');
  }
};
